import { useEffect, useState } from 'react';
import { Card, Spinner, Table } from 'react-bootstrap';
import type { FulfillmentAdminClient, StockLedgerRow } from '@/infrastructure/FulfillmentAdminClient';
import type { CatalogHit } from '@/pages/fulfillment/components/catalogSearch';
import { catalogHitLabel } from '@/pages/fulfillment/components/catalogSearch';
import { kstTimestamp } from '@/utils/fulfillmentTime';

export interface PickedProductStockProps {
  client: FulfillmentAdminClient;
  product: CatalogHit;
}

/** The fulfillment book for one sku, summed across its shelves the way Current Stocks sums it. */
interface StockTotals {
  onHand: number;
  reserved: number;
  shelves: Array<{ locationCode: string; onHand: number }>;
}

/** Enough arrivals to tell a regular inbound from a first one without turning the form into Stock History. */
export const RECENT_INBOUND_LIMIT = 5;

/** `undefined` while the read is in flight, `null` once it has failed. */
type Read<T> = T | null | undefined;

const readStock = async (client: FulfillmentAdminClient, sku: string): Promise<StockTotals | null> => {
  try {
    const { rows } = await client.listInventory({ skus: [sku], includeEmpty: true });
    return {
      onHand: rows.reduce((total, row) => total + row.onHand, 0),
      reserved: rows.reduce((total, row) => total + row.reserved, 0),
      shelves: rows
        .filter((row) => row.onHand !== 0)
        .map((row) => ({ locationCode: row.locationCode, onHand: row.onHand })),
    };
  } catch {
    return null;
  }
};

const readRecentInbound = async (client: FulfillmentAdminClient, sku: string): Promise<StockLedgerRow[] | null> => {
  try {
    const { rows } = await client.getStockLedger({ sku, movementType: 'INBOUND', limit: RECENT_INBOUND_LIMIT });
    return rows;
  } catch {
    return null;
  }
};

/**
 * What the warehouse already holds of the product just picked, and when it last arrived, so a requester
 * who found it by name or SKU (no 88-code to scan) can judge the order quantity before registering.
 * Read-only: both halves are informational, so a failed read says so and never blocks the request.
 */
export const PickedProductStock = ({ client, product }: PickedProductStockProps) => {
  const [stock, setStock] = useState<Read<StockTotals>>(undefined);
  const [inbound, setInbound] = useState<Read<StockLedgerRow[]>>(undefined);

  useEffect(() => {
    // A newer pick replaces this one; its slower answers must not paint over the product now on screen.
    let current = true;
    setStock(undefined);
    setInbound(undefined);
    void readStock(client, product.sku).then((result) => {
      if (current) {
        setStock(result);
      }
    });
    void readRecentInbound(client, product.sku).then((result) => {
      if (current) {
        setInbound(result);
      }
    });
    return () => {
      current = false;
    };
  }, [client, product.sku]);

  const available = stock ? stock.onHand - stock.reserved : 0;

  return (
    <Card className="mb-2" role="region" aria-label="Picked product stock">
      <Card.Body className="py-2 small">
        <div className="fw-semibold mb-1">
          {catalogHitLabel(product)} <span className="text-muted font-monospace">{product.sku}</span>
        </div>
        <div className="d-flex flex-wrap gap-4">
          <div>
            <div className="text-muted text-uppercase">Current stock</div>
            {stock === undefined && <Spinner animation="border" size="sm" role="status" />}
            {stock === null && <div className="text-muted">Current stock is unavailable right now.</div>}
            {stock && (
              <>
                <div>
                  On hand <b>{stock.onHand}</b> · Reserved <b>{stock.reserved}</b> · Available{' '}
                  {/* Derived like Current Stocks: the server floors each shelf at 0, which would hide an oversell. */}
                  <b className={available < 0 ? 'text-danger' : undefined}>
                    {available < 0 ? `⚠ ${available}` : available}
                  </b>
                </div>
                <div className="text-muted">
                  {stock.shelves.length > 0
                    ? stock.shelves.map((shelf) => `${shelf.locationCode}: ${shelf.onHand}`).join(' · ')
                    : 'Not on any shelf'}
                </div>
              </>
            )}
          </div>
          <div className="flex-grow-1">
            <div className="text-muted text-uppercase">Recent inbound</div>
            {inbound === undefined && <Spinner animation="border" size="sm" role="status" />}
            {inbound === null && <div className="text-muted">Recent inbound is unavailable right now.</div>}
            {inbound && inbound.length === 0 && <div className="text-muted">No inbound recorded for this SKU yet.</div>}
            {inbound && inbound.length > 0 && (
              <Table size="sm" className="mb-0" aria-label="Recent inbound">
                <thead>
                  <tr>
                    <th>Received</th>
                    <th>Qty</th>
                    <th>Location</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {inbound.map((row) => (
                    <tr key={`${row.occurredAt}#${row.refId ?? ''}#${row.locationCode}`}>
                      <td>{kstTimestamp(row.occurredAt)}</td>
                      <td>+{Math.abs(row.delta)}</td>
                      <td>{row.locationCode}</td>
                      <td>{row.actor ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
